import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import { OneTimeCodeField } from '@/components/fields/one-time-code-field';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { useVerifyOtp } from '@/hooks/auth/use-request-otp';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';

export default function SecuritySettingsIndexRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: verifyOtp, isPending } = useVerifyOtp();

  useAdjustLayout();

  const form = useForm<BeginUpdatePasswordFormData>({
    resolver: zodResolver(beginUpdatePasswordFormScheme),
    defaultValues: {
      countryCode: '86',
      phoneNumber: '',
      otpCode: '',
    },
  });

  async function handleVerifyOtpCode(data: BeginUpdatePasswordFormData) {
    const res = await verifyOtp({
      Phone: data.phoneNumber,
      TelCode: data.countryCode,
      Code: data.otpCode,
    });

    if (res.Code === 0) {
      navigate(ROUTES.MY.SETTINGS.SECURITY.PASSWORD.$path());
    }
  }

  useEffect(() => {
    if (userInfo?.Phone && userInfo?.TelCode) {
      form.reset({
        countryCode: userInfo.TelCode,
        phoneNumber: userInfo.Phone,
      });
    }
  }, [userInfo?.Phone, userInfo?.TelCode]);

  return (
    <PageLayout
      title={t('mine.security')}
      headerConfig={{
        navigateBack: true,
      }}>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(handleVerifyOtpCode)}
          className='mx-5 flex flex-1 flex-col justify-between'>
          <div>
            <label
              htmlFor='phoneNumber'
              className='form-control w-full'>
              <div className='label px-0'>
                <span className='label-text'>{t('security.phone')}</span>
              </div>

              <p className='text-body1 text-secondary'>{`${form.watch('countryCode')} ${form.watch('phoneNumber')}`}</p>
            </label>

            <hr className='my-4' />

            <h4 className='mb-2 font-bold'>{t('security.password-not-set')}</h4>

            <label className='form-control w-full'>
              <div className='label px-0'>
                <span className='label-text'>{t('security.otp-code')}</span>
              </div>

              <OneTimeCodeField
                id='otpCode'
                name='otpCode'
                placeholder={t('auth.one-time-password')}
                type='sign-in'
                inputClassName='bg-transparent'
              />

              <FieldMessage
                name='otpCode'
                errors={form.formState.errors}
              />
            </label>
          </div>

          <ProgressButton
            as='submit'
            isLoading={isPending}
            disabled={!form.formState.isDirty}
            className='btn btn-primary'
            label='auth.next-step'
          />
        </form>
      </FormProvider>
    </PageLayout>
  );
}

const beginUpdatePasswordFormScheme = z.object({
  countryCode: z.string(),
  phoneNumber: z.string().min(8, 'errors.string.length|count=8').regex(/^\d+$/),
  otpCode: z.string().length(6, 'errors.string.length|count=6'),
});

type BeginUpdatePasswordFormData = z.infer<typeof beginUpdatePasswordFormScheme>;
